import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "How do I find a SAINT® TMS provider near me?",
    answer:
      "Search the Magnus Medical official provider locator, which lists clinics authorized to deliver the SAINT® protocol. SAINT® availability is more limited than conventional TMS because it requires the Magnus Neuromodulation System, fMRI targeting, and manufacturer-certified training. If no provider is nearby, many patients travel — the 5-day schedule makes travel practical.",
  },
  {
    question: "Can I get SAINT® TMS as an outpatient?",
    answer:
      "Yes. SAINT® is delivered in an outpatient setting. Patients are awake during treatment, no anesthesia is used, and most can drive themselves home. Neuro Wellness TMS Centers of America delivers SAINT® in a private outpatient clinic in Coral Springs, Florida.",
  },
  {
    question: "What should I ask a SAINT® provider before starting?",
    answer:
      "Ask: Do you use the Magnus Neuromodulation System? Do you use fMRI-guided targeting? Are you listed on the Magnus Medical provider locator? What does the evaluation include? What is the total cost? What insurance do you accept? What is the follow-up plan?",
  },
];

export const pageHowToFindSaintProvider: LearnPageData = {
  slug: "how-to-find-a-saint-tms-provider",
  title: "How to Find a SAINT® TMS Provider | Neuro Wellness",
  description:
    "How to find and verify a SAINT® TMS provider: check the Magnus Medical locator, ask the right questions, and understand why SAINT availability is limited.",
  canonical: "/learn/how-to-find-a-saint-tms-provider",
  h1: "How to Find a SAINT® TMS Provider",
  breadcrumbLabel: "How to Find a SAINT® Provider",
  crumbs: baseCrumbs("How to Find a SAINT® Provider"),
  shortAnswer:
    "To find a SAINT® TMS provider, search the Magnus Medical official provider locator, which lists clinics authorized to deliver the SAINT® protocol. SAINT® availability is more limited than conventional TMS because it requires the Magnus Neuromodulation System, fMRI targeting, and manufacturer-certified training. If no provider is nearby, many patients travel — the 5-day schedule makes travel practical.",
  keyTakeaways: [
    "Start with the Magnus Medical official provider locator.",
    "SAINT® requires specialized equipment and training not available at most TMS clinics.",
    "Ask whether the clinic uses the Magnus Neuromodulation System and fMRI targeting.",
    "The 5-day schedule makes traveling for treatment practical.",
    "Verify the provider's listing — do not rely on self-asserted claims.",
  ],

  sections: [
    {
      heading: "Step 1: Check the Magnus Medical Locator",
      body: (
        <>
          {P(
            "The most reliable way to find a SAINT® provider is the Magnus Medical official provider locator on their website. This lists clinics that have completed manufacturer-certified training and use the Magnus Neuromodulation System. Do not rely solely on a clinic's own website claims — verify on the official locator.",
          )}
        </>
      ),
    },
    {
      heading: "Step 2: Ask the Right Questions",
      body: (
        <>
          {P(
            "When you contact a clinic, ask: Do you use the Magnus Neuromodulation System? Do you use fMRI-guided targeting? Are you listed on the Magnus Medical provider locator? What does the evaluation include? What is the total cost and what insurance do you accept? What is the follow-up plan after the 5 days?",
          )}
        </>
      ),
    },
    {
      heading: "Step 3: Consider Traveling",
      body: (
        <>
          {P(
            "Because SAINT® is delivered over 5 consecutive days rather than 6 weeks of daily visits, traveling for treatment is practical for many patients. Neuro Wellness TMS Centers of America in Coral Springs, Florida treats patients from across Florida and out of state. Plan to arrive the day before, stay locally for the week, and arrange follow-up care with your home providers.",
          )}
          {P(
            <>
              <a
                href="/traveling-for-treatment"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Learn about traveling for treatment
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Magnus Medical — SAINT® Provider Locator.",
      url: "https://www.magnusmedical.com/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/how-to-find-a-saint-tms-provider#webpage`,
        name: "How to Find a SAINT® TMS Provider",
        url: `${SITE_URL}/learn/how-to-find-a-saint-tms-provider`,
        description: "How to find and verify a SAINT TMS provider.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/how-to-find-a-saint-tms-provider#faq`),
      breadcrumbJsonld(baseCrumbs("How to Find a SAINT® Provider")),
    ],
  },
  relatedLinks: [
    {
      title: "Find a Verified SAINT® Provider",
      href: "/find-a-verified-saint-provider",
      description: "Our official Magnus Medical listing",
    },
    {
      title: "What Is a Verified SAINT® Provider?",
      href: "/learn/what-is-a-verified-saint-provider",
      description: "What verification means",
    },
    {
      title: "Where Can I Get SAINT® TMS?",
      href: "/learn/where-can-i-get-saint-tms",
      description: "Finding SAINT treatment",
    },
    {
      title: "Traveling for Treatment",
      href: "/traveling-for-treatment",
      description: "Planning your trip",
    },
  ],
};
